import { router } from "@inertiajs/react"
import { ArrowLeft, FileText, Sparkles, Type, Volume2 } from "lucide-react"
import { useState } from "react"

import AudioPlayer from "@/components/conversation/audio-player"
import { RelatedCards } from "@/components/flashcards/related-cards"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { useFlashcardProps } from "@/hooks/use-flashcard-props"
import AppLayout from "@/layouts/app-layout"
import { cn } from "@/lib/utils"
import type { Flashcard } from "@/types"

interface Props {
  flashcard: Flashcard
}

export default function FlashcardShow(props: unknown) {
  const { flashcard } = useFlashcardProps<Props>(props)
  return <FlashcardShowContent flashcard={flashcard} />
}

const QUALITY_LEVELS = [
  {
    value: 1,
    label: "Again",
    description: "Complete blackout",
    color: "bg-red-600 hover:bg-red-700",
  },
  {
    value: 3,
    label: "Hard",
    description: "Correct with serious difficulty",
    color: "bg-orange-500 hover:bg-orange-600",
  },
  {
    value: 4,
    label: "Good",
    description: "Correct with hesitation",
    color: "bg-blue-600 hover:bg-blue-700",
  },
  {
    value: 5,
    label: "Easy",
    description: "Perfect recall",
    color: "bg-green-600 hover:bg-green-700",
  },
] as const

function FlashcardShowContent({ flashcard }: Props) {
  const [isRevealed, setIsRevealed] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleQualitySubmit = (quality: number) => {
    if (isSubmitting) return

    setIsSubmitting(true)
    router.post(
      `/flashcards/${flashcard.uuid}/practice`,
      { quality },
      {
        onSuccess: () => {
          // CACHE-BUST: Inertia's browser history restoration ignores preserveState: false.
          // Adding a timestamp query param forces a fresh server request with updated audioStatus.
          //
          // Future optimization: Instead of cache-busting, we could:
          // 1. Use ActionCable to push audioStatus updates to index page in real-time
          // 2. Implement Inertia partial reloads for just the flashcard data
          // 3. Store audioStatus in client-side state that persists across navigations
          router.visit(`/flashcards?_t=${Date.now()}`, { preserveState: false })
        },
        onError: () => {
          setIsSubmitting(false)
        },
      },
    )
  }

  return (
    <AppLayout
      breadcrumbs={[
        { title: "Flashcards", href: "/flashcards" },
        { title: "Practice", href: `/flashcards/${flashcard.uuid}` },
      ]}
    >
      <div className="flex min-h-[calc(100vh-4rem)] flex-col">
        {/* Minimal Header */}
        <div className="border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
          <div className="mx-auto flex max-w-4xl items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              // See CACHE-BUST note in handleQualitySubmit
              onClick={() =>
                router.visit(`/flashcards?_t=${Date.now()}`, {
                  preserveState: false,
                })
              }
            >
              <ArrowLeft className="mr-2 size-4" />
              Exit
            </Button>
            <div className="text-xs text-slate-500">
              {flashcard.repetitions} reviews • Ease{" "}
              {flashcard.easeFactor.toFixed(1)}
            </div>
          </div>
        </div>

        {/* Flashcard - Full Screen Focus */}
        <div className="flex flex-1 items-center justify-center p-4">
          <div className="w-full max-w-2xl">
            <Card className="shadow-lg">
              <CardContent className="p-8 sm:p-12 md:p-16">
                {!isRevealed ? (
                  /* Front */
                  <div className="space-y-8 text-center">
                    <div className="inline-flex items-center gap-2 rounded-full bg-yellow-50 px-3 py-1 dark:bg-yellow-900/20">
                      <Sparkles className="size-4 text-yellow-600 dark:text-yellow-500" />
                      <span className="text-xs font-medium tracking-wide text-yellow-700 uppercase dark:text-yellow-500">
                        Question
                      </span>
                    </div>
                    <p className="min-h-[4rem] text-3xl leading-tight font-semibold break-words text-slate-900 sm:text-4xl md:text-5xl dark:text-white">
                      {flashcard.frontText}
                    </p>
                    <Button
                      size="lg"
                      onClick={() => setIsRevealed(true)}
                      className="mt-8 px-12 py-6 text-lg"
                    >
                      Show Answer
                    </Button>
                  </div>
                ) : (
                  /* Back - Question + Answer */
                  <div className="space-y-6 text-center">
                    {/* Question (Context) */}
                    <div className="space-y-3">
                      <div className="inline-flex items-center gap-2 rounded-full bg-yellow-50 px-3 py-1 dark:bg-yellow-900/20">
                        <Sparkles className="size-3.5 text-yellow-600 dark:text-yellow-500" />
                        <span className="text-xs font-medium tracking-wide text-yellow-700 uppercase dark:text-yellow-500">
                          Question
                        </span>
                      </div>
                      <p className="text-lg break-words text-slate-600 dark:text-slate-400">
                        {flashcard.frontText}
                      </p>
                    </div>

                    {/* Visual Separator */}
                    <div className="mx-auto h-px w-24 bg-gradient-to-r from-transparent via-slate-300 to-transparent dark:via-slate-600" />

                    {/* Answer (Hero) */}
                    <div className="space-y-6">
                      <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 dark:bg-blue-900/20">
                        <Volume2 className="size-4 text-blue-600 dark:text-blue-500" />
                        <span className="text-xs font-medium tracking-wide text-blue-700 uppercase dark:text-blue-500">
                          Answer
                        </span>
                      </div>

                      {flashcard.backRomanization && (
                        <p className="min-h-[4rem] text-4xl leading-tight font-bold break-words text-slate-900 sm:text-5xl md:text-6xl dark:text-white">
                          {flashcard.backRomanization}
                        </p>
                      )}

                      <p className="text-2xl break-words text-slate-600 dark:text-slate-400">
                        {flashcard.backText}
                      </p>

                      {flashcard.backAudioUrl && (
                        <div className="mx-auto w-full max-w-md">
                          <AudioPlayer
                            audioUrl={flashcard.backAudioUrl}
                            variant="dark"
                            className="w-full px-4 py-3"
                          />
                        </div>
                      )}

                      {flashcard.notes && (
                        <div className="mt-6 rounded-lg bg-slate-50 p-4 text-left dark:bg-slate-800">
                          <p className="text-sm break-words text-slate-700 dark:text-slate-300">
                            <span className="font-medium">Notes: </span>
                            {flashcard.notes}
                          </p>
                        </div>
                      )}

                      {/* Related Cards Section */}
                      {((flashcard.wordFlashcards?.length ?? 0) > 0 ||
                        (flashcard.phraseFlashcards?.length ?? 0) > 0) && (
                        <div className="mt-8 space-y-4 text-left">
                          <Separator />
                          <div className="text-muted-foreground flex items-center gap-2 text-sm">
                            {flashcard.cardType === "phrase" ? (
                              <FileText className="size-4" />
                            ) : (
                              <Type className="size-4" />
                            )}
                            <Badge variant="outline" className="capitalize">
                              {flashcard.cardType}
                            </Badge>
                          </div>

                          {flashcard.wordFlashcards &&
                            flashcard.wordFlashcards.length > 0 && (
                              <RelatedCards
                                cards={flashcard.wordFlashcards}
                                direction="words"
                                maxVisible={5}
                              />
                            )}

                          {flashcard.phraseFlashcards &&
                            flashcard.phraseFlashcards.length > 0 && (
                              <RelatedCards
                                cards={flashcard.phraseFlashcards}
                                direction="phrases"
                                maxVisible={5}
                              />
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Quality Rating - Fixed Bottom */}
        {isRevealed && (
          <div className="border-t border-slate-200 bg-white px-4 py-4 dark:border-slate-700 dark:bg-slate-900">
            <div className="mx-auto max-w-4xl">
              <div className="mb-3 text-center">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  How well did you know this?
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
                {QUALITY_LEVELS.map((level) => (
                  <button
                    key={level.value}
                    onClick={() => handleQualitySubmit(level.value)}
                    disabled={isSubmitting}
                    className={cn(
                      "rounded-lg p-3 text-center font-semibold text-white transition-all disabled:cursor-not-allowed disabled:opacity-50 sm:p-4",
                      level.color,
                    )}
                  >
                    <div className="text-base sm:text-lg">{level.label}</div>
                    <div className="mt-1 text-xs opacity-90">
                      {level.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
